/**
 * Date display/parsing based on student is_US_user preference.
 * Storage and APIs always use ISO YYYY-MM-DD.
 * US display/input: YYYY-DD-MM (year-day-month).
 */

export function isUSUserFlag(user) {
  if (!user) return false;
  const v = user.is_US_user ?? user.is_us_user;
  return v === true || v === 'true' || v === 1;
}

export function normalizeIsoDateString(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Display ISO date for text inputs (default YYYY-MM-DD, US YYYY-DD-MM). */
export function isoDateToDisplay(iso, isUS) {
  const normalized = normalizeIsoDateString(iso);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return String(iso || '');
  const [y, m, d] = normalized.split('-');
  return isUS ? `${y}-${d}-${m}` : normalized;
}

function parseUsYearDayMonth(text) {
  const match = String(text).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const month = parseInt(match[3], 10);
  if (
    year >= 1900 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= 31
  ) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

export function displayDateToIso(text, isUS) {
  const s = String(text || '').trim();
  if (!s) return null;

  if (isUS) {
    const fromUs = parseUsYearDayMonth(s);
    if (fromUs) return fromUs;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const a = parseInt(slash[1], 10);
    const b = parseInt(slash[2], 10);
    const year = parseInt(slash[3], 10);
    const month = isUS ? a : b;
    const day = isUS ? b : a;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return null;
}

export function dateInputPlaceholder(isUS) {
  return isUS ? 'YYYY-DD-MM' : 'YYYY-MM-DD';
}

export function formatDateLabelFromIso(iso, isUS) {
  const key = normalizeIsoDateString(iso);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return String(iso || '');
  if (isUS) return isoDateToDisplay(key, true);
  const d = new Date(`${key}T12:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateForLocaleDisplay(value, isUS) {
  if (value == null || value === '') return '';
  const iso = normalizeIsoDateString(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return isoDateToDisplay(iso, isUS);
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return isoDateToDisplay(normalizeIsoDateString(d), isUS);
  } catch {
    return String(value);
  }
}

export function formatDateObjectForInput(dateObj, isUS) {
  return isoDateToDisplay(normalizeIsoDateString(dateObj), isUS);
}

export function parseFlexibleDateInput(dateString, isUS) {
  if (!dateString) return null;
  const iso = displayDateToIso(dateString, isUS);
  if (iso) return new Date(`${iso}T12:00:00`).toISOString();

  const formats = [/^(\w+)\s+(\d{4})$/];
  for (const format of formats) {
    const match = dateString.match(format);
    if (match) {
      const [, monthName, year] = match;
      const monthMap = {
        jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
        apr: 3, april: 3, may: 4, jun: 5, june: 5,
        jul: 6, july: 6, aug: 7, august: 7, sep: 8, september: 8,
        oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
      };
      const month = monthMap[monthName.toLowerCase()];
      if (month !== undefined) {
        return new Date(year, month, 1).toISOString();
      }
    }
  }

  return null;
}

const LONG_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Ordinal suffix for a day of month (1st, 2nd, 3rd, 4th, …). */
export function ordinalSuffix(day) {
  const n = Number(day);
  if (!Number.isFinite(n)) return 'th';
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** Long display from ISO date, e.g. 15th June 2026. */
export function isoDateToLongDisplay(iso) {
  const normalized = normalizeIsoDateString(iso);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return String(iso || '');
  const parts = parseIsoToParts(normalized);
  if (!parts) return normalized;
  const { year, monthIndex, day } = parts;
  const monthName = LONG_MONTH_NAMES[monthIndex];
  if (!monthName) return normalized;
  return `${day}${ordinalSuffix(day)} ${monthName} ${year}`;
}

export function isoFromParts(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseIsoToParts(iso) {
  const normalized = normalizeIsoDateString(iso);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const [y, m, d] = normalized.split('-').map((n) => parseInt(n, 10));
  return { year: y, monthIndex: m - 1, day: d };
}

export function localTodayIsoDate() {
  const now = new Date();
  return isoFromParts(now.getFullYear(), now.getMonth(), now.getDate());
}
